import { Dialog, Button } from '@mui/material';
import { getEventsLegends, getLayerCounter, getLegends } from "api/layer";
import { ReactComponent as LocationsSvg } from "assets/icon_locations.svg";
import Options from "components/options/options";
import SubOptions from "components/options/sub-options";
import Svg from "components/svg/svg";
import { RightPanel, Option } from "helpers/constants";
import { AppAction, AppContext } from "helpers/context";
import { handleFilterLocations } from "helpers/helpers";
import { MapContext } from "helpers/map";
import { EventData } from 'helpers/models';
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./multiple-analyse-modal.module.scss";
import SelectEvent from '../select-event/select-event';
import SelectLayer from "../select-layer/select-layer";
import {
	buildClimateRiskScores,
	CRS_RESPONSE_STRUCTURE,
	FATHOM_LAYER_IDS
} from "../shared/analysis-shared";
import {
	executeMultipleAnalysisWithReporting,
	syncMultipleAnalysisRunningStatus,
	MultipleAnalysisParams
} from "../shared/multiple-analysis-executor";

function MultipleAnalyseModal() {
	type MultipleSubOption = "natural-hazard" | "climate-risk-score";

	const [context, dispatch] = useContext(AppContext);
	const [option, setOption] = useState<Option>(Option.HAZARD);
	const [subOption, setSubOption] = useState<MultipleSubOption>("natural-hazard");
	const [t] = useTranslation();
	const [fathomValidateStatus, setFathomValidateStatus] = useState(false);
	const [hazardValidateStatus, setHazardValidateStatus] = useState(false);
	const [crsValidateStatus, setCrsValidateStatus] = useState(false);
	const [eventsValidateStatus, setEventsValidateStatus] = useState(false);

	const { AMap, map } = useContext(MapContext);
	const [hazardButtons, setHazardButtons] = useState<any[]>([]);
	const [climateButtons, setClimateButtons] = useState<any[]>([]);

	const [selectedButtons, setSelectedButtons] = useState<string[][]>(
		context.multipleAnalyseModal?.selectId
			? [[context.multipleAnalyseModal.selectId], []]
			: [[], []]
	);
	const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
	const locationsTable = handleFilterLocations({
		data: context.currentMultipleLocations?.items || [],
		unit: context.currentMultipleLocations?.aggregationUnit ?? "numberOfLocations",
		multipleLocationsSearch: context.multipleLocationsSearch,
		locationsFilterInfo: context.locationsFilterInfo,
		filterChecked: true
	});

	const onOpen = () => {
		dispatch({ type: AppAction.setSelectLayer, payload: selectedButtons });
		dispatch({ type: AppAction.setSelectedEvent, payload: selectedEvent });
		if (selectedButtons[0].length && !context.askAgain) {
			const fathomLayer = selectedButtons[0].find((e) => FATHOM_LAYER_IDS.includes(e))

			if (fathomLayer) {
				close();
				dispatch({ type: AppAction.setMultipleFathomPopup, payload: "locations" });
				return;
			}
		}
		runAnalysis();
	}

	/** 多点分析入口，逻辑与 single-analyse-modal 保持一致 */
	const runAnalysis = async (selBtns = selectedButtons, selEvt = selectedEvent) => {
		if (context.rightPanel === RightPanel.Multiple || context.rightPanel === RightPanel.Events) {
			if (context.currentMultipleLocationsAnalysed && context.currentMultipleLocations && context.currentMultipleLocationsAnalysed.id !== context.currentMultipleLocations.id) {
				dispatch({ type: AppAction.setHazardMultipleAnalysedLegends, payload: undefined });
				dispatch({ type: AppAction.setHazardMultipleAnalysisStore, payload: undefined });
				dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: undefined });
				dispatch({ type: AppAction.setClimateMultipleAnalysedLegends, payload: undefined });
				dispatch({ type: AppAction.setClimateMultipleAnalysisResult, payload: undefined });
				dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: undefined });
				dispatch({ type: AppAction.setEventAnalysisLegend, payload: undefined });
				dispatch({ type: AppAction.setEventAnalysisStore, payload: undefined });
				dispatch({ type: AppAction.setEventAnalysisStatus, payload: undefined });
			}
			context.rightPanel === RightPanel.Events && dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple })

			let params: MultipleAnalysisParams = {
				eventLayers: [],
				catNetLayers: [],
				responseStructure: [],
				climateRiskScores: [],
				locations: [],
			};
			if (selBtns[0].length) {
				params.catNetLayers = selBtns[0];
				dispatch({
					type: AppAction.setHazardMultipleAnalysedLegends,
					payload: context.hazardLegends?.filter((item) => {
						return selBtns[0].includes(item.layerId);
					}),
				});
			}

			if (selBtns[1].length) {
				params.climateRiskScores = buildClimateRiskScores(selBtns[1]);
				params.responseStructure = [CRS_RESPONSE_STRUCTURE];
				dispatch({
					type: AppAction.setClimateMultipleAnalysedLegends,
					payload: context.crsLayerList?.filter((item) => {
						return selBtns[1].includes(item.id);
					}),
				});
			}

			if (selEvt?.id && selEvt?.layerId) {
				params.eventLayers = [selEvt.layerId];
				dispatch({
					type: AppAction.setSelectedEvent,
					payload: selEvt,
				});
				if (context.eventsList?.length && !selEvt.checked) {
					selEvt.checked = true;
					if (selEvt.layerObj) {
						selEvt.layerObj.show()
						let findLengend = context.eventsLegend?.find((e: any) => e.layerInfo.layerId === selEvt.layerId);
						dispatch({ type: AppAction.setEventAnalysisLegend, payload: [{ ...findLengend, layerInfo: selEvt }] })
						dispatch({ type: AppAction.updateEventsLegend, payload: selEvt });
					} else {
						const layerObj = new AMap.TileLayer({
							tileUrl: `${(window as any).amapLayerUrl}/risklayer-api/catnet/tiles/${selEvt.layerId}/tile/[z]/[x]/[y]?X-Token=${sessionStorage.getItem("token")}`,
							opacity: selEvt.opacity,
						});
						selEvt.layerObj = layerObj;
						map.addLayer(layerObj);
						getEventsLegends({ layers: selEvt.layerId }).then(async (res: any) => {
							if (res.legends?.length) {
								dispatch({ type: AppAction.setEventAnalysisLegend, payload: [{ ...res.legends[0], layerInfo: selEvt }] })
								dispatch({ type: AppAction.addEventsLegend, payload: { ...res.legends[0], layerInfo: selEvt } })
							}
						});
					}
					const updatedEvent = context.eventsList.map(e => {
						if (e.layerId === selEvt.layerId) {
							return { ...e, checked: true }
						}
						return e;
					})
					dispatch({ type: AppAction.setSelectedEventsList, payload: [...context.selectedEventsList, selEvt] });
					dispatch({ type: AppAction.setEventsList, payload: updatedEvent });
				} else {
					let findLengend = context.eventsLegend?.find((e: any) => e.layerInfo.layerId === selEvt.layerId);
					dispatch({ type: AppAction.setEventAnalysisLegend, payload: [{ ...findLengend, layerInfo: selEvt }] })
				}
			}

			locationsTable.forEach((item) => {
				params.locations?.push({
					id: item.id,
					coordinates: {
						latitude: item.latitudeWgs,
						longitude: item.longitudeWgs,
					},
				});
			});

			if (context.currentMultipleLocationsAnalysed?.id !== context.currentMultipleLocations?.id) {
				dispatch({ type: AppAction.setHazardMultipleAnalysisStore, payload: undefined });
				dispatch({ type: AppAction.setClimateMultipleAnalysisResult, payload: undefined });
				dispatch({ type: AppAction.setEventAnalysisStore, payload: undefined });
			} else {
				params.catNetLayers = params.catNetLayers?.filter((item) => {
					let layerMap = context.hazardMultipleAnalysisStore?.get(item);
					if (layerMap) {
						let analysisResultNum = params.locations?.filter((location) => layerMap?.get(location.id.split("-").join(""))).length;
						return analysisResultNum != params.locations?.length
					} else {
						return true;
					}
				});

				params.climateRiskScores = params.climateRiskScores?.filter((item) => {
					if (context.climateMultipleAnalysisResult?.[0]?.climateRiskScores) {
						let analysisResultNum = context.climateMultipleAnalysisResult?.filter((result) => {
							if (params.locations?.find((location) => location.id.split("-").join("") === result.id.split("-").join(""))) {
								return result.climateRiskScores.find((layers) => layers.index === item.index)
							} else {
								return false
							}
						}).length
						return analysisResultNum != params.locations?.length
					} else {
						return true;
					}
				});

				params.eventLayers = params.eventLayers?.filter((item) => {
					let layerMap = context.eventAnalysisStore?.get(item);
					if (layerMap) {
						let analysisResultNum = params.locations?.filter((location) => layerMap?.get(location.id.split("-").join(""))).length;
						return analysisResultNum != params.locations?.length
					} else {
						return true;
					}
				});
			}

			syncMultipleAnalysisRunningStatus(params, dispatch);

			close();
			dispatch({ type: AppAction.setCurrentMultipleLocationsAnalysed, payload: { ...context.currentMultipleLocations } });
			dispatch({ type: AppAction.setSingleLocationAnalysed, payload: false });
			dispatch({ type: AppAction.setMultipleLocationsAnalysed, payload: true });
			executeMultipleAnalysisWithReporting(params, dispatch);
		}
	};

	const close = (): void => {
		dispatch({ type: AppAction.setMultipleAnalyseModal, payload: undefined });
		handleRevert()
	};

	useEffect(() => {
		if (option === Option.HAZARD) {
			setSubOption("natural-hazard");
		} else if (option === Option.CLIMATE) {
			setSubOption("climate-risk-score");
		}
	}, [option]);

	useEffect(() => {
		if (context.tryAgainType === "analyse" && context.rightPanel !== RightPanel.Single) {
			runAnalysis(context.selectLayer, context.selectedEvent)
		}
	}, [context.tryAgainType])

	useEffect(() => {
		if (context?.layerList?.length) {
			const hazardLegendsIds: string[] = [];
			let hazardPermission = false,
				fathomPermission = false;
			if (context.multipleAnalyseModal) {
				hazardPermission = context.menu.find((item) => item.id === "103002" && item.status === "1")
				fathomPermission = context.menu.find((item) => item.id === "103005" && item.status === "1")
			}

			let arr =
				context?.layerList
					?.map((e) => e.children)
					.flat()
					.map((e) => {
						hazardLegendsIds.push(e.id);
						return {
							id: e.id,
							title: e.name,
						validateStatus: e.id.includes("Fathom") ? fathomValidateStatus : hazardValidateStatus,
						disabled: e.id.includes("Fathom") ? (!fathomPermission || fathomValidateStatus) : (!hazardPermission || hazardValidateStatus),
						icon: Svg({ icon: e.id, disabled: e.id.includes("Fathom") ? (!fathomPermission || fathomValidateStatus) : (!hazardPermission || hazardValidateStatus) }),
						};
					}) || [];
			setHazardButtons(arr);
			if (!context.hazardLegends) {
				dispatch({ type: AppAction.setHazardLegends, payload: [] });
				getLegends({
					layers: hazardLegendsIds.join(","),
				}).then((res: any) => {
					dispatch({ type: AppAction.setHazardLegends, payload: res.data.legends });
				})
			}
		}
	}, [context.menu, context.multipleAnalyseModal, context.layerList, fathomValidateStatus, hazardValidateStatus]);

	useEffect(() => {
		if (context?.crsLayerList?.length) {
			const climateLegendsIds: string[] = [];
			let crsPermission = false;
			if (context.multipleAnalyseModal) {
				crsPermission = context.menu.find((item) => item.id === "103003" && item.status === "1")
			}

			let arr =
				context?.crsLayerList?.map((e) => {
					climateLegendsIds.push(e.id);
					return {
						id: e.id,
						title: e.name,
						validateStatus: crsValidateStatus,
						disabled: !crsPermission || crsValidateStatus,
						icon: Svg({ icon: e.id, disabled: !crsPermission || crsValidateStatus }),
					};
				}) || [];
			// arr = arr.filter(e => e.id !== "wind")
			setClimateButtons(arr);
		}
	}, [context.menu, context.multipleAnalyseModal, context.crsLayerList, crsValidateStatus]);

	useEffect(() => {
		if (!context.menu?.length) {
			return;
		}

		if (context.multipleAnalyseModal === undefined) {
			return;
		}
		let locations: any = [];
		if (context.rightPanel === RightPanel.Single) {
			if (context.currentMultipleLocationInfo) {
				locations = [context.currentMultipleLocationInfo.longitudeWgs + "," + context.currentMultipleLocationInfo.latitudeWgs];
			} else if (context.searchLocationInfo) {
				locations = [context.searchLocationInfo?.[context.language]?.location?.split(",")[0] + "," + context.searchLocationInfo?.[context.language]?.location?.split(",")[1]];
			}
		} else if (context.rightPanel === RightPanel.Multiple) {
			if (context.currentMultipleLocations) {
				context.currentMultipleLocations.items!.forEach((item) => {
					if (item.checked) {
						locations.push(item.longitudeWgs + "," + item.latitudeWgs);
					}
				});
			}
		}

		getLayerCounter({ actionCategories: "fathom,nonFathom,crs,event" }, locations).then(res => {
			// existInCache: 非 fathom 数据是否命中缓存
			// existInEventCache: event 数据是否命中缓存
			// existInFathomCache: fathom 数据是否命中缓存
			setFathomValidateStatus(!res.data.existInFathomCache && res.data.itemFathomQuotas !== -1 && ((res.data.itemFathomQuotas ?? 0) <= (res.data.trItemFathomCount ?? 0)));
			setHazardValidateStatus(!res.data.existInCache && res.data.itemQuotas !== -1 && ((res.data.itemQuotas ?? 0) <= (res.data.trItemCount ?? 0)));
			setCrsValidateStatus(!res.data.existInCRSCache && res.data.itemCRSQuotas !== -1 && ((res.data.itemCRSQuotas ?? 0) <= (res.data.trItemCRSCount ?? 0)));
			setEventsValidateStatus(!res.data.existInEventCache && res.data.itemEventQuotas !== -1 && ((res.data.itemEventQuotas ?? 0) <= (res.data.trItemEventCount ?? 0)));
		})

		if (context.menu.find((item) => item.id === "103002" && item.status === "1") || context.menu.find((item) => item.id === "103005" && item.status === "1")) {
			setOption(Option.HAZARD);
			return;
		}

		if (context.menu.find((item) => item.id === "103003" && item.status === "1")) {
			setOption(Option.CLIMATE);
			return;
		}
	}, [context.menu, context.multipleAnalyseModal]);

	const handleRevert = () => {
		setSelectedButtons([[], []]);
		setSelectedEvent(null);
	};

	const isAnySelected = selectedButtons.flatMap((arr) => arr).length || !!selectedEvent;

	return (
		<Dialog
			open={!!context.multipleAnalyseModal}
			onClose={close}
			sx={{
				"& .MuiDialog-paper": {
					width: "780px",
					maxWidth: "80vw",
					maxHeight: "85vh"
				}
			}}
		>
			<div className={styles.container}>
				<div className={styles.header}>{t("analyseModal.analyseLocations")}</div>
				<div className={styles.analyse}>
					<div>
						<div className={styles.header}>
							<LocationsSvg className={styles.image} />
							<div>
								{context.currentMultipleLocations && (
									<div>
										<div className={styles.headerName}>{context.currentMultipleLocations!.name}</div>
										<div className={styles.headerText}>
											{locationsTable.length}/{context.currentMultipleLocations?.itemCount}
											{t("analyseModal.locations")}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="mt-5 mb-2">
						<Options
							selected={option}
							onChange={setOption}
						/>
					</div>
					{option !== Option.EVENT && (
						<div className="mt-5 mb-2">
							<SubOptions
								selected={subOption}
								onChange={(value) => setSubOption(value as MultipleSubOption)}
								customItems={
									option === Option.HAZARD
										? [{ value: "natural-hazard", label: t("options.single.hazards") }]
										: [{ value: "climate-risk-score", label: t("options.single.climateRiskScores") }]
								}
							/>
						</div>
					)}
					<div className={styles.tabContent}>
						{option === Option.HAZARD && (
							<SelectLayer
								index={0}
								buttons={hazardButtons}
								selectedButtons={selectedButtons}
								setSelectedButtons={setSelectedButtons}
								type="multiple"
							/>
						)}
						{option === Option.CLIMATE && (
							<SelectLayer
								index={1}
								buttons={climateButtons}
								selectedButtons={selectedButtons}
								setSelectedButtons={setSelectedButtons}
								type="multiple"
							/>
						)}
						{option === Option.EVENT && (
							<SelectEvent
								isDisabled={eventsValidateStatus}
								selectedEvent={selectedEvent}
								setSelectedEvent={setSelectedEvent}
							/>
						)}
					</div>
				</div>
				<div className="flex justify-between mt-5 mb-5">
					<div>
						<Button
							onClick={handleRevert}
							variant="text"
							color="success"
						>
							{t("buttons.reset")}
						</Button>
					</div>
					<div>
						<Button
							onClick={close}
							variant="text"
							color="success"
						>
							{t("buttons.close")}
						</Button>
						<Button
							color="success"
							variant="contained"
							disabled={!isAnySelected}
							onClick={() => onOpen()}
						>
							{t("buttons.runAnalysis")}
						</Button>
					</div>
				</div>
			</div>
		</Dialog >
	);
}

export default MultipleAnalyseModal;

